// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.
import React, { ErrorInfo } from 'react';
import { AzureLogger, createClientLogger } from '@azure/logger';
import { ErrorFallback } from './ErrorFallback';

type AzureCommunicationErrorBoundaryProps = {
  fallbackElement?: React.ReactNode;
  children: React.ReactNode;
};

type AzureCommunicationErrorBoundaryState = {
  hasError: boolean;
};

/**
 * @private
 */
export class AzureCommunicationErrorBoundary extends React.Component<
  AzureCommunicationErrorBoundaryProps,
  AzureCommunicationErrorBoundaryState
> {
  _logger: AzureLogger;

  constructor(props: AzureCommunicationErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
    this._logger = createClientLogger('communication-react:component');
    this._logger.error.enabled = true;
    this._logger.verbose.enabled = true;
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // You can also log the error to an error reporting service
    this._logger.error(`Uncaught error for component! \n Error: ${error} \n Error info: ${info.componentStack}`);
    this.setState({ hasError: true });
  }

  render(): React.ReactNode {
    if (this.state.hasError) {
      return this.props.fallbackElement ?? <ErrorFallback />;
    }

    return this.props.children;
  }
}
